
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { Template } from "@/lib/types";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import TemplateForm, { templateFormSchema, TemplateFormValues } from "./TemplateForm";
import { createTemplate, updateTemplate } from "@/services/templateService";
import { useIsMobile } from "@/hooks/use-mobile";

interface TemplateDrawerProps {
  tenantId: string;
  template?: Template | null;
  mode: 'create' | 'edit';
  onSuccess: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerButton?: boolean;
}

const TemplateDrawer: React.FC<TemplateDrawerProps> = ({
  tenantId,
  template = null,
  mode = 'create',
  onSuccess,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  triggerButton = true
}) => {
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const { toast } = useToast();
  
  // Determine if the component is controlled or uncontrolled
  const isControlled = controlledOpen !== undefined && setControlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? setControlledOpen : setInternalOpen;
  
  // Set up default values based on mode
  const defaultValues = {
    name: "",
    html_content: "&nbsp;",
  };
  
  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: defaultValues,
  });
  
  // Reset form when drawer opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && template) {
        form.reset({
          name: template.name,
          html_content: template.html_content,
        });
      } else if (mode === 'create') {
        form.reset(defaultValues);
      }
    }
  }, [form, mode, open, template]);
  
  const onSubmit = async (values: TemplateFormValues) => {
    console.log("Form submission values:", values);
    try {
      if (mode === 'create') {
        // Create new template
        await createTemplate({
          tenant_id: tenantId,
          name: values.name,
          html_content: values.html_content,
          attachments: [], // Keep empty array for future use
        });
        
        toast({
          title: "Template created",
          description: "Your template has been created successfully.",
        });
        
        // Don't reset the form here, as we're about to close the drawer
      } else {
        // Update existing template
        if (!template) return;
        
        await updateTemplate(template.id, {
          ...values,
          attachments: template.attachments || [], // Keep existing attachments array in the database
        });
        
        toast({
          title: "Template updated",
          description: "Your template has been updated successfully.",
        });
      }
      
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error("Template submission error:", error);
      toast({
        title: "Error",
        description: `Failed to ${mode === 'create' ? 'create' : 'update'} template. Please try again.`,
        variant: "destructive",
      });
    }
  };

  // Calculate width based on whether it's mobile or desktop
  const sheetWidthClass = isMobile ? "w-full" : "w-2/5";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {triggerButton && mode === 'create' && (
        <SheetTrigger asChild>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Template
          </Button>
        </SheetTrigger>
      )}
      <SheetContent
        side="right"
        className={`p-0 rounded-none border-l shadow-lg ${sheetWidthClass} flex flex-col h-full`}
        style={{ transform: "translateX(0)" }}
      >
        <SheetHeader className="p-6 border-b">
          <SheetTitle>{mode === 'create' ? 'Add New Template' : 'Edit Template'}</SheetTitle>
          <SheetDescription>
            {mode === 'create' 
              ? 'Create a new email template for automated replies.'
              : 'Update your email template.'}
          </SheetDescription>
        </SheetHeader>
        
        <ScrollArea className="flex-grow px-6 py-4">
          <TemplateForm form={form} />
        </ScrollArea>
        
        <SheetFooter className="p-6 border-t mt-auto">
          <div className="flex justify-end gap-2 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={form.handleSubmit(onSubmit, (errors) => {
                // Show validation errors as toast when form submission fails
                const errorMessages = [];
                if (errors.name) errorMessages.push("Template name is required");
                if (errors.html_content) errorMessages.push("HTML content is required");
                
                if (errorMessages.length > 0) {
                  toast({
                    title: "Please complete all required fields",
                    description: errorMessages.join(", "),
                    variant: "destructive",
                  });
                }
              })}
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting 
                ? (mode === 'create' ? "Creating..." : "Saving...")
                : (mode === 'create' ? "Create Template" : "Save Changes")}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default TemplateDrawer;
